import './MiceActivityTable.scss';
import React, { useState, useEffect } from 'react';
import AnimatedNumber from 'animated-number-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import MiceActivityTableTitle from './MiceActivityTableTitle';

import { Period } from 'components/AnalyticsPage/Periods';
import useGetMiceActivityTableData from 'hooks/Analitics/MCTCharts/3_miceActivityDonutChart/useGetMiceActivityTableData';
import { TrapType } from 'components/AnalyticsPage/TrapType';
import { ObjectTraps } from 'api/analitics/types';

interface TableProps {
  slug: string;
  type: TrapType;
  objectTraps: ObjectTraps;
}

const MiceActivityTable: React.FC<TableProps> = ({ slug, type, objectTraps }) => {
  const [trapType, setTrapType] = useState<TrapType>(type === TrapType.ALL ? TrapType.MCT : type);
  const [period, setPeriod] = useState<Period>(Period.THREE_MONTH);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showCustom, setShowCustom] = useState<boolean>(false);
  const { t } = useTranslation();
  // Initialize dates based on default period
  useEffect(() => {
    const threeMonthsAgo = dayjs().subtract(3, 'month').format('YYYY-MM-DD');
    const today = dayjs().format('YYYY-MM-DD');
    setStartDate(threeMonthsAgo);
    setEndDate(today);
  }, []);

  const { tableData, isLoading, isFetching, refetch } = useGetMiceActivityTableData(
    slug,
    trapType,
    startDate,
    endDate,
    !!startDate && !!endDate,
  );

  const formatNumber = (number: number): string => {
    return number.toLocaleString('en-US', { useGrouping: true }).replace(/,/g, ' ');
  };

  return (
    <div className="card card-height-100 m-0 border border-2 rounded-4 position-relative">
      <div className="card-header rounded-4">
        <MiceActivityTableTitle
          title={
            trapType === TrapType.SRST
              ? t('ChartsAnalitics.peak_activity_periods_table.title')
              : trapType === TrapType.MCT
                ? t('ChartsAnalitics.peak_activity_periods_table.title_mct')
                : t('ChartsAnalitics.peak_activity_periods_table.title_smst')
          }
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          period={period}
          setPeriod={setPeriod}
          showCustom={showCustom}
          setShowCustom={setShowCustom}
          refetch={refetch}
          showSwitchTrapType={
            objectTraps.mct.was_ever_installed && objectTraps.smst.was_ever_installed && type !== TrapType.SRST
          }
          switchTrapType={trapType}
          setSwitchTrapType={(value: TrapType) => setTrapType(value)}
          switchTrapTypeLoading={isFetching}
        />
      </div>
      <div className="card-body body-height">
        <div className="table-responsive">
          <div className="scrollable-table-container">
            {isLoading || isFetching ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th className="text-center">{t('ChartsAnalitics.peak_activity_periods_table.col_1')}</th>
                    <th className="text-center">{t('ChartsAnalitics.peak_activity_periods_table.col_2')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.time_period.length > 0 ? (
                    tableData.time_period.map((period, index) => (
                      <tr key={index}>
                        <td className="text-center">{period}</td>
                        <td className="text-center">
                          <span
                            className={`badge border ${tableData.count[index] > 0 ? 'text-danger border-danger' : 'border-success text-success'} fs-12`}
                          >
                            <AnimatedNumber
                              value={tableData.count[index]}
                              formatValue={formatNumber}
                              duration={1000}
                              className="animated-number"
                            />{' '}
                            {t('ChartsAnalitics.global.mice')}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} className="text-center">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiceActivityTable;
